<template>
  <div
    v-click-outside="close"
    :class="[
      'ue-select bg-white rounded position-relative',
      { 'opacity-75 pe-none cursor-not-allowed': disabled },
    ]"
    :aria-disabled="disabled"
  >
    <input
      v-if="searchable"
      :id="'input-' + keyField"
      ref="searchInput"
      type="text"
      class="ue-select__search-input form-control px-2 border rounded text-truncate"
      :placeholder="placeholder"
      v-model="searchTerm"
      @focus="open"
      @input="onSearchInput"
      :disabled="disabled"
    />

    <div
      v-else
      :id="'input-' + keyField"
      class="ue-select__input d-flex align-items-center justify-content-between px-2 border rounded overflow-hidden text-truncate"
      @click="toggle"
    >
      <span class="text-truncate" v-if="!value">{{ placeholder }}</span>
      <span class="text-truncate" v-else>{{ value[labelField] }}</span>
      <div class="d-flex align-items-center ml-2">
        <button
          v-if="clearable && value"
          type="button"
          class="close ue-select__clear-btn mr-2"
          @click.stop="clear"
          :disabled="disabled"
        >
          <span aria-hidden="true">&times;</span>
        </button>
        <span><i class="bi bi-chevron-down"></i></span>
      </div>
    </div>

    <ul
      v-if="isOpen"
      class="ue-select__options list-unstyled rounded border position-absolute bg-white w-500 shadow-sm p-1"
      :style="{ 'overscroll-behavior': overscrollBehavior }"
      @wheel.stop
    >
      <li
        v-for="opt in options"
        :key="opt[keyField]"
        class="ue-select__option rounded p-2 text-truncate d-flex align-items-center justify-content-between"
        @click.stop="select(opt)"
      >
        <div class="text-truncate">
          <slot name="option" :option="opt">
            {{ opt[labelField] }}
          </slot>
        </div>
        <div class="selected-indicator d-flex align-items-center justify-content-end">
          <i
            v-if="value && opt[keyField] === value[keyField]"
            class="bi bi-check-lg text-primary"
          ></i>
        </div>
      </li>
    </ul>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import { PropType } from 'vue'
import clickOutside from '@/lib/directives/clickOutside'

/**
 * Represents one selectable option.
 * @typedef {object} Option
 * @property {any} [key: string] - Additional fields representing option data.
 */
interface Option {
  [key: string]: any
}

/**
 * UeSelect — a lightweight dropdown component for single selection,
 * with optional search input when `searchable` is true.
 *
 * Renders a list of Option objects, displays the current selection,
 * and emits the chosen item via v-model/input. If `searchable` is set,
 * shows an input and emits `search` events instead of toggle-on-click.
 * @example
 *
 * <UeSelect
 *   searchable
 *   clearable
 *   overscrollBehavior="auto"
 *   @search="onSearchTerm"
 *   :options="users"
 *   v-model="selectedUser"
 *   labelField="username"
 *   keyField="id"
 *   placeholder="Search users..."
 *   :disabled="isDisabled"
 *   @input="onUserSelected"
 * />
 */
export default Vue.extend({
  name: 'UeSelect',

  directives: { clickOutside },

  model: {
    prop: 'value',
    event: 'input',
  },

  props: {
    /**
     * Currently selected option (two-way bound via v-model).
     */
    value: {
      type: Object as PropType<Option | null>,
      default: null,
    },

    /**
     * List of option objects to display.
     */
    options: {
      type: Array as PropType<Option[]>,
      required: true,
    },

    /**
     * Key in each option object to display as label.
     */
    labelField: {
      type: String,
      default: 'label',
    },

    /**
     * Key in each option object to use as unique identifier.
     */
    keyField: {
      type: String,
      default: 'code',
    },

    /**
     * Text shown when no option is selected or used as input placeholder.
     */
    placeholder: {
      type: String,
      default: '',
    },

    /**
     * Prevents interaction when true.
     */
    disabled: {
      type: Boolean,
      default: false,
    },

    /**
     * If true, renders a text input and emits `search` events on input.
     */
    searchable: {
      type: Boolean,
      default: false,
    },

    /**
     * If true, shows a clear button to unselect the current option.
     */
    clearable: {
      type: Boolean,
      default: false,
    },

    /**
     * Controls the overscroll behavior of the dropdown options list.
     */
    overscrollBehavior: {
      type: String,
      default: 'contain',
    },
  },

  data() {
    return {
      /**
       * Controls visibility of dropdown list.
       */
      isOpen: false,
      /**
       * Bound to search input when `searchable` is true.
       */
      searchTerm: '',
    }
  },

  methods: {
    /**
     * Toggle the dropdown open state, unless disabled or in searchable mode.
     */
    toggle(): void {
      if (!this.disabled && !this.searchable) {
        this.isOpen = !this.isOpen
      }
    },

    /**
     * Open the dropdown list (used when focusing search input).
     */
    open(): void {
      if (!this.disabled) {
        this.isOpen = true
      }
    },

    /**
     * Close the dropdown list.
     */
    close(): void {
      this.isOpen = false
    },

    /**
     * Emit a search event with the current input value and keep dropdown open.
     */
    onSearchInput(): void {
      this.$emit('search', this.searchTerm)
      this.isOpen = true
    },

    /**
     * Select an option, emit it via v-model/input, then close.
     * @param option — the Option object that was clicked
     */
    select(option: Option): void {
      if (!this.disabled && !option.isDisabled) {
        this.$emit('input', option)

        if (this.searchable) {
          this.searchTerm = option[this.labelField]
        }

        this.close()
      }
    },

    /**
     * Clear the current selection by emitting null.
     */
    clear(): void {
      if (!this.disabled) {
        this.$emit('input', null)

        if (this.searchable) {
          this.searchTerm = ''
        }
      }
    },
  },
})
</script>

<style lang="sass" scoped>
.ue-select
  cursor: pointer
  user-select: none

  &__input
    height: 2.25rem

  &__search-input
    height: 2.25rem

  &.cursor-not-allowed
    cursor: not-allowed

  &__options
    margin-top: 0.25rem
    max-height: 200px
    overflow-y: auto
    z-index: 10
    min-width: 100%
    max-width: 40rem

    // Slim scrollbar styles
    scrollbar-width: thin
    scrollbar-color: #cbd5e0 transparent

    &::-webkit-scrollbar
      width: 6px

    &::-webkit-scrollbar-track
      background: transparent

    &::-webkit-scrollbar-thumb
      background-color: #cbd5e0
      border-radius: 3px

      &:hover
        background-color: #a0aec0

  &__option
    cursor: pointer

    .selected-indicator
      width: 1.5rem

    &:hover
      background-color: var(--gray-100)

    &--selected
      background-color: var(--gray-100)

  &__clear-btn.close
    font-size: 1.2rem
</style>
